import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  InGameCurrency,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { Energy } from '@sorare/core/src/atoms/icons/Energy';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { ANY_SPORT_MY_CARDS_ESSENCE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useGetMyCardsBoostLink } from '@sorare/core/src/hooks/my-cards/useGetMyCardsBoostLink';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';

export enum InGameCurrencyAgreggated {
  ENERGY = 'ENERGY',
  XP = 'XP',
  SHARDS = 'SHARDS',
}

type CurrencyData = {
  id: InGameCurrencyAgreggated;
  icon: ReactNode;
  amount?: number;
  visibleOnSports: Sport[];
  expanded?: boolean;
  onClick?: () => void;
};
export const useUserCurrencies = (
  expandedCurrencies?: InGameCurrencyAgreggated[]
): CurrencyData[] => {
  const navigate = useNavigate();
  const { sport } = useSportContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { currentUser } = useCurrentUserContext();
  const { energyBalance, xpBalances, shardsChests } = currentUser || {};

  const filteredXpBalances =
    currentUser?.betaTesterFootballCommonCardsGame || sport !== Sport.FOOTBALL
      ? xpBalances
      : xpBalances?.filter(
          balance => balance.currency !== InGameCurrency.COMMON_XP
        );
  const totalEnergyBalance = energyBalance?.reduce(
    (sum, { amount }) => sum + amount,
    0
  );
  const totalShards = shardsChests?.reduce(
    (acc, chest) => acc + chest.cardShardsCount,
    0
  );
  const firstNonZeroBalance = (filteredXpBalances || []).find(
    ({ amount }) => amount > 0
  );
  const firstNonZeroBalanceRarity = firstNonZeroBalance?.currency
    ? inGameCurrencyToRarity[firstNonZeroBalance.currency] || Rarity.limited
    : Rarity.limited;
  const totalXPBalance = filteredXpBalances?.reduce(
    (sum, { amount }) => sum + amount,
    0
  );

  return [
    {
      id: InGameCurrencyAgreggated.XP,
      icon: <Boost size={2} color="var(--c-rivals-friendlies)" />,
      amount: totalXPBalance,
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.XP),
      onClick: () =>
        navigate(
          getMyCardsBoostLink({
            rarity: firstNonZeroBalanceRarity,
            sport,
          })
        ),
    },
    {
      id: InGameCurrencyAgreggated.SHARDS,
      icon: <Shards size={2} color="var(--c-rivals-win)" />,
      amount: totalShards,
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.SHARDS),
      onClick: () =>
        navigate(generateSportPath(ANY_SPORT_MY_CARDS_ESSENCE, { sport })),
    },
    {
      id: InGameCurrencyAgreggated.ENERGY,
      icon: <Energy size={2} />,
      amount: totalEnergyBalance,
      visibleOnSports: [Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.ENERGY),
    },
  ];
};
